import { useState, useEffect, Fragment } from 'react';
import { apiFetch } from '../api';
import KPICard from '../components/KPICard';
import DataFreshness from '../components/DataFreshness';
import Dropdown from '../components/Dropdown';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import { formatDate } from '../utils/formatDate';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
// Region pie chart only — reuses ShopifyTracker.jsx's FulfillmentPie styling
// verbatim (confirmed choice). Everything else on this page stays on Nivo;
// this is a deliberate, confirmed exception for one chart, not drift.
import { PieChart, Pie, Cell, Legend, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { useSwrStore, useSwrQuery } from '../swrCache';

// Portal's actual brand palette (frontend/src/sidebarTokens.css --sbn-purple-*
// scale, 600 = #6E58A6) — NOT the ad-hoc teal-first COLORS array the old
// Sales pages used locally.
const PURPLE_600 = '#6E58A6';
const PURPLE_300 = '#C4B4E4';
const TEXT_MUTED  = '#736A85';

// One distinct color per channel in the platform bar chart — stays in the
// brand family (built off PURPLE_600) rather than Nivo's default category10
// scheme, so it doesn't clash with the purple-only trend chart next to it.
const CHANNEL_COLORS = ['#6E58A6', '#9A7BC4', '#3A6B8A', '#5C7E60', '#9A7B3E', '#C25E77', '#4A5A8A', '#7E5C8A'];

// Header bar — ported as-is from GRNManagement.jsx's HeaderBeamBackground
// (animata/background/animated-beam.tsx technique: fixed diagonal lanes,
// ~30% carrying a randomized "meteor" streak on a seeded-random loop). Kept
// scoped to this page via an embedded <style> tag exactly like GRN does, so
// it can't leak into other routes. Only the header piece is ported (not
// GRN's page-wide dark theme/blob background/status tabs) — user asked
// specifically for "that kind of header block".
const prand = (n) => {
    const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
    return x - Math.floor(x);
};
const lerp = (min, max, t) => min + (max - min) * t;
const HEADER_LANES = 40;

function HeaderBeam({ index }) {
    const fast = prand(index + 91) < 0.3;
    const duration = fast ? lerp(1.2, 3.2, prand(index)) : lerp(6, 14, prand(index));
    const delay = lerp(0, 9, prand(index + 13));
    const length = Math.round(lerp(10, 26, prand(index + 29)));
    const width = Math.max(2, Math.round(lerp(2, 4, prand(index + 53))));
    const opacity = fast ? lerp(0.32, 0.5, prand(index + 71)) : lerp(0.14, 0.32, prand(index + 71));
    return (
        <div
            className="ov-beam"
            style={{
                '--duration': `${duration.toFixed(2)}s`, '--delay': `${delay.toFixed(2)}s`,
                '--length': `${length}px`, '--opacity': opacity.toFixed(2), width: `${width}px`,
            }}
        />
    );
}
function HeaderBeamBackground() {
    return (
        <div className="ov-beam-bg" aria-hidden="true">
            {Array.from({ length: HEADER_LANES }, (_, i) => (
                <div key={i} className="ov-beam-lane">
                    {prand(i + 7) < 0.3 && <HeaderBeam index={i + 1} />}
                </div>
            ))}
        </div>
    );
}
const HEADER_CSS = `
.ov-beam-bg { position: absolute; inset: 0; display: flex; overflow: hidden; border-radius: 16px; z-index: 0; pointer-events: none; background: #0B0A12; }
.ov-beam-lane { position: relative; flex: 1 0 auto; width: 34px; height: 100%; }
.ov-beam-lane::before { content: ''; position: absolute; inset: 0; left: 50%; width: 1px; background: rgba(230,222,250,0.06); transform: rotate(10deg); }
.ov-beam {
  position: absolute; left: 50%; top: -20%; width: 2px; height: var(--length);
  transform: translateX(-50%) rotate(10deg); opacity: 0;
  background: linear-gradient(180deg, transparent, #E4DBF5, transparent);
  animation: ov-meteor var(--duration) var(--delay) ease-in-out infinite backwards;
}
@keyframes ov-meteor {
  0%   { top: -20%; opacity: 0; }
  12%  { opacity: var(--opacity); }
  88%  { opacity: var(--opacity); }
  100% { top: 120%; opacity: 0; }
}
@media (prefers-reduced-motion: reduce) { .ov-beam { animation: none; opacity: 0; } }

/* .kpi-card's global teal (border-top + .sub color, see App.css) is shared
   with pages that haven't been redesigned yet — overridden here, scoped to
   this page only, rather than touching the shared class. Growth is already
   color-coded via the badge; the sub caption itself is just neutral text. */
.sales-overview-page .kpi-card { border-top-color: #E4DCF0; }
.sales-overview-page .kpi-card .sub { color: #8C82A0; }

/* Loading overlay — only shown on a true fetch with nothing cached yet (see
   useSwrQuery below); a cache-hit background refresh never blurs the page,
   that would defeat the point of caching. */
.ov-loading-overlay { position: absolute; inset: 0; z-index: 10; display: flex; align-items: center; justify-content: center; }

/* From Uiverse.io by ShaikhWahid99 */
.liquid-loader { display: flex; flex-direction: column; align-items: center; gap: 5px; padding: 20px; font-family: system-ui, sans-serif; }
.loader-track {
  position: relative; width: 180px; height: 32px;
  background: linear-gradient(135deg, #2a2a2a, #1a1a1a);
  border-radius: 16px; overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.6), 0 1px 3px rgba(255, 255, 255, 0.1);
}
.liquid-fill {
  position: absolute; top: 2px; left: 2px; height: calc(100% - 4px);
  background: linear-gradient(90deg, #4f46e5, #7c3aed, #ec4899, #f59e0b);
  border-radius: 14px;
  animation: fillProgress 4s ease-out infinite, colorShift 3s linear infinite;
  box-shadow: 0 0 12px rgba(124, 58, 237, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2);
}
/* Original Uiverse widget is white-on-dark-page; this page's overlay sits on
   a light blurred backdrop, so the label is recolored dark to stay legible
   — everything else (track, fill, animations) is unchanged from the source. */
.loading-text { color: #3D3452; font-size: 18px; font-weight: 600; letter-spacing: 1px; animation: textGlow 1s ease-in-out infinite; }
.dot { margin-left: 3px; animation: blink 1.5s infinite; }
.dot:nth-of-type(1) { animation-delay: 0s; }
.dot:nth-of-type(2) { animation-delay: 0.3s; }
.dot:nth-of-type(3) { animation-delay: 0.6s; }
@keyframes fillProgress { 0% { width: 4px; } 25% { width: 25%; } 50% { width: 50%; } 75% { width: 75%; } 100% { width: calc(100% - 4px); } }
@keyframes colorShift { 0% { filter: hue-rotate(0deg) brightness(1); } 33% { filter: hue-rotate(120deg) brightness(1.1); } 66% { filter: hue-rotate(240deg) brightness(0.9); } 100% { filter: hue-rotate(360deg) brightness(1); } }
@keyframes textGlow { 0%, 100% { opacity: 0.7; text-shadow: 0 0 8px rgba(139, 92, 246, 0.3); } 50% { opacity: 1; text-shadow: 0 0 16px rgba(139, 92, 246, 0.6); } }
@keyframes blink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }
@media (prefers-reduced-motion: reduce) { .liquid-fill, .loading-text, .dot { animation: none; } }
`;

const RANGE_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'this_quarter', label: 'This Quarter' },
    { value: 'calendar_year', label: 'Calendar Year' },
    { value: 'financial_year', label: 'Financial Year' },
    { value: 'custom', label: 'Custom Range' },
];

function fmt(n) {
    if (n === null || n === undefined) return '—';
    if (n >= 10000000) return '₹' + (n / 10000000).toFixed(1) + ' Cr';
    if (n >= 100000)   return '₹' + (n / 100000).toFixed(1) + ' L';
    return '₹' + Number(n).toLocaleString('en-IN');
}

// Full, unabbreviated number for the hover tooltip on abbreviated KPI values.
function fullFmt(n) {
    if (n === null || n === undefined) return '—';
    return '₹' + Number(n).toLocaleString('en-IN');
}

function growthBadge(n) {
    if (n === null || n === undefined) return null;
    return { text: (n > 0 ? '+' : '') + n + '%', positive: n > 0 ? true : n < 0 ? false : null };
}

// Column widths shared between the header row and each MoverRow so labels
// line up with their values.
const MOVER_QTY_COL = 64;
const MOVER_TREND_COL = 64;
const MOVER_GROWTH_COL = 50;

function MoverHeader() {
    return (
        <div style={{ display: 'flex', alignItems: 'center', padding: '0 0 6px', fontSize: 10.5, fontWeight: 700, color: '#A79CBD', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            <span style={{ flex: 1 }} />
            <span style={{ width: MOVER_QTY_COL, textAlign: 'right' }}>Prev Qty</span>
            <span style={{ width: MOVER_QTY_COL, textAlign: 'right' }}>Curr Qty</span>
            <span style={{ width: MOVER_TREND_COL, margin: '0 10px' }} />
            <span style={{ width: MOVER_GROWTH_COL, textAlign: 'right' }}>Growth</span>
        </div>
    );
}

function MoverRow({ r, positive }) {
    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', padding: '9px 0', borderBottom: '1px solid #F1F5F9', fontSize: 13 }}>
            <span style={{
                color: '#1E293B', flex: 1, minWidth: 0, marginRight: 10,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>{r.name}</span>
            <span style={{ width: MOVER_QTY_COL, textAlign: 'right', color: '#8C82A0', fontSize: 12.5, flexShrink: 0 }}>{r.qty_prev.toLocaleString('en-IN')}</span>
            <span style={{ width: MOVER_QTY_COL, textAlign: 'right', color: '#1E293B', fontSize: 12.5, fontWeight: 600, flexShrink: 0 }}>{r.qty_curr.toLocaleString('en-IN')}</span>
            <span style={{ width: MOVER_TREND_COL, margin: '0 10px', flexShrink: 0 }}>
                <SparklineInline data={r.trend} color={positive ? '#10B981' : '#EF4444'} width={MOVER_TREND_COL} />
            </span>
            <span style={{ fontWeight: 700, color: positive ? '#10B981' : '#EF4444', width: MOVER_GROWTH_COL, textAlign: 'right', flexShrink: 0 }}>{positive ? '+' : ''}{r.growth}%</span>
        </div>
    );
}

// Smooth quadratic-bezier path through the midpoints between consecutive
// points — a standard lightweight sparkline-smoothing technique (no curve
// library needed) — plus a thicker stroke, per feedback that the plain
// polyline read as too thin/jagged.
function smoothPath(pts) {
    if (pts.length < 2) return '';
    let d = `M ${pts[0][0]},${pts[0][1]}`;
    for (let i = 1; i < pts.length; i++) {
        const [px, py] = pts[i - 1];
        const [x, y] = pts[i];
        const mx = (px + x) / 2, my = (py + y) / 2;
        d += ` Q ${px},${py} ${mx},${my}`;
    }
    const [lx, ly] = pts[pts.length - 1];
    d += ` L ${lx},${ly}`;
    return d;
}

function SparklineInline({ data, color, width = 56, height = 20 }) {
    if (!data || data.length < 2) return <span style={{ display: 'inline-block', width, height }} />;
    const min = Math.min(...data), max = Math.max(...data);
    const range = max - min || 1;
    const pts = data.map((v, i) => [
        (i / (data.length - 1)) * width,
        height - ((v - min) / range) * (height - 4) - 2,
    ]);
    return (
        <svg width={width} height={height}>
            <path d={smoothPath(pts)} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

// Overview — Sales Analytics dashboard landing page.
export default function Dashboard() {
    const [allPlatforms, setAllPlatforms] = useState([]);

    // Committed filters — these (not the draft ones below) feed the cache
    // key/actual query, so changing a filter never re-fetches until Search
    // is pressed.
    const [range, setRange] = useState('last_30_days');
    const [selectedPlatforms, setSelectedPlatforms] = useState([]);
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');

    // Draft filters — written to by the filter controls; copied into the
    // committed state above only when Search is clicked.
    const [draftRange, setDraftRange] = useState(range);
    const [draftPlatforms, setDraftPlatforms] = useState(selectedPlatforms);
    const [draftCustomFrom, setDraftCustomFrom] = useState('');
    const [draftCustomTo, setDraftCustomTo] = useState('');

    function runSearch() {
        setRange(draftRange);
        setSelectedPlatforms(draftPlatforms);
        setCustomFrom(draftCustomFrom);
        setCustomTo(draftCustomTo);
    }

    useEffect(() => {
        apiFetch('sales-filters').then(d => setAllPlatforms(d.platforms || [])).catch(console.error);
    }, []);

    const store = useSwrStore();
    const platformsKey = selectedPlatforms.join(',');
    const isCustom = range === 'custom' && customFrom && customTo;
    const cacheKey = `range=${range}&platforms=${platformsKey}&from=${isCustom ? customFrom : ''}&to=${isCustom ? customTo : ''}`;
    const { data: result, loading, error: fetchError, fetchedAt, refresh } = useSwrQuery(store, cacheKey, async () => {
        const params = { range };
        if (selectedPlatforms.length) params.platforms = platformsKey;
        if (isCustom) { params.date_from = customFrom; params.date_to = customTo; }
        const [overview, platformBoard, locations] = await Promise.all([
            apiFetch('sales-overview', params), apiFetch('sales-platforms', params), apiFetch('sales-locations', params),
        ]);
        return { overview, platformBoard, locations };
    });
    const data = result?.overview;
    const platformData = result?.platformBoard;
    const locations = result?.locations;
    const error = fetchError ? 'Could not load the dashboard — the database may be slow right now. Try again in a moment.' : '';
    // Blur + centered dots only on a true fetch with nothing cached yet — a
    // background revalidation (loading true but result already present)
    // renders the existing content instantly with no blur.
    const showLoadingOverlay = loading && !result;

    const platformOptions = allPlatforms.map(p => ({ value: p, label: p }));
    const topPlatforms = (platformData?.board || []).slice(0, 7);
    const growthByPlatform = Object.fromEntries(topPlatforms.map(p => [p.platform, p.growth]));

    return (
        <div className="sales-overview-page">
            <style>{HEADER_CSS}</style>
            <div style={{
                position: 'relative', display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
                background: '#0B0A12', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 6px 20px rgba(35,28,48,0.10)', padding: '14px 20px', marginBottom: 18,
            }}>
                {/* .ov-beam-bg (below) clips its own contents with its own
                    overflow:hidden — deliberately NOT set here, since this
                    container also hosts the Dropdown/MultiSelectDropdown
                    popovers, which would otherwise get clipped too. */}
                <HeaderBeamBackground />
                <div style={{ position: 'relative', zIndex: 1 }}>
                    <h1 style={{ fontWeight: 700, fontSize: 22, margin: 0, color: '#F3EFFA', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>Sales Dashboard</h1>
                    {data && <p style={{ margin: '2px 0 0', fontSize: 12.5, color: '#A79CBD' }}>{data.month_label}</p>}
                </div>
                <div data-tutorial="sales-date-range" style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end' }}>
                    <DataFreshness fetchedAt={fetchedAt} loading={loading} onRefresh={refresh} style={{ color: '#A79CBD' }} />
                    <Dropdown value={draftRange} options={RANGE_OPTIONS} onChange={setDraftRange} />
                    {draftRange === 'custom' && (
                        <>
                            <input type="date" value={draftCustomFrom} onChange={e => setDraftCustomFrom(e.target.value)}
                                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #E4DCF0', fontSize: 13 }} />
                            <span style={{ color: '#A79CBD', fontSize: 12 }}>to</span>
                            <input type="date" value={draftCustomTo} onChange={e => setDraftCustomTo(e.target.value)}
                                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #E4DCF0', fontSize: 13 }} />
                        </>
                    )}
                    {platformOptions.length > 0 && (
                        <MultiSelectDropdown values={draftPlatforms} options={platformOptions} onChange={setDraftPlatforms} placeholder="Platforms" />
                    )}
                    <button
                        type="button"
                        onClick={runSearch}
                        style={{
                            padding: '7px 18px', borderRadius: 999, border: 'none', background: PURPLE_600,
                            color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        }}
                    >
                        Search
                    </button>
                </div>
            </div>

            <div style={{ position: 'relative' }}>
                {showLoadingOverlay && (
                    <div className="ov-loading-overlay">
                        <div className="liquid-loader">
                            <div className="loader-track"><div className="liquid-fill" /></div>
                            <div className="loading-text">Loading<span className="dot">.</span><span className="dot">.</span><span className="dot">.</span></div>
                        </div>
                    </div>
                )}
                <div style={showLoadingOverlay ? { filter: 'blur(4px)', pointerEvents: 'none' } : undefined}>
            {error ? <p className="empty" style={{ color: '#EF4444' }}>{error}</p> : !data ? <p className="loading">Loading overview...</p> : (
                <>
                    <div className="kpi-row">
                        <KPICard label="Total Revenue" value={fmt(data.kpis.revenue)} fullValue={fullFmt(data.kpis.revenue)} badge={growthBadge(data.kpis.revenue_growth)} sub={data.kpis.comparison_label} sparkline={data.trend.map(t => t.revenue)} />
                        <KPICard label="Gross MRP Value" value={fmt(data.kpis.revenue_mrp)} fullValue={fullFmt(data.kpis.revenue_mrp)} badge={growthBadge(data.kpis.revenue_mrp_growth)} sub={data.kpis.comparison_label} />
                        <KPICard label="Units Sold" value={data.kpis.units.toLocaleString('en-IN')} badge={growthBadge(data.kpis.units_growth)} sub={data.kpis.comparison_label} sparkline={data.trend.map(t => t.units)} />
                        <KPICard label="Best Platform" value={data.kpis.best_platform || '—'} sub={data.kpis.best_platform_units != null ? `${data.kpis.best_platform_units.toLocaleString('en-IN')} units` : '—'} sparkline={data.kpis.best_platform_trend} />
                        <KPICard label="Active SKUs" value={data.kpis.active_skus.toLocaleString('en-IN')} badge={growthBadge(data.kpis.active_skus_growth)} sub="sold this period" sparkline={data.trend.map(t => t.active_skus)} />
                        {locations?.best_city && (
                            <KPICard
                                label="Best Seller City (QC)"
                                value={locations.best_city.city}
                                sub={`${locations.best_city.qty.toLocaleString('en-IN')} units · ${locations.best_city.share}%`}
                            />
                        )}
                        {data.latest_date && (
                            <KPICard
                                label="Data As Of"
                                value={formatDate(data.latest_date)}
                                sub="Latest date in the data"
                            />
                        )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '60% 1fr', gap: 16, marginBottom: 20 }}>
                        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '18px 20px' }}>
                            <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: '#0F1F2E' }}>Revenue — this period {data.kpis.comparison_label}</h3>
                            <div style={{ height: 300 }}>
                                <ResponsiveLine
                                    data={[
                                        { id: 'This period', data: data.trend.map(t => ({ x: t.label, y: t.revenue })) },
                                        { id: 'Preceding period', data: data.trend.map(t => ({ x: t.label, y: t.revenue_prev })) },
                                    ]}
                                    margin={{ top: 20, right: 30, bottom: 40, left: 70 }}
                                    xScale={{ type: 'point' }}
                                    yScale={{ type: 'linear', min: 0, max: 'auto' }}
                                    curve="monotoneX"
                                    colors={[PURPLE_600, PURPLE_300]}
                                    lineWidth={3}
                                    enablePoints={false}
                                    enableArea={true}
                                    areaOpacity={1}
                                    defs={[
                                        {
                                            id: 'revenueGradient', type: 'linearGradient', colors: [
                                                { offset: 0, color: PURPLE_600, opacity: 0.28 },
                                                { offset: 100, color: PURPLE_600, opacity: 0 },
                                            ],
                                        },
                                    ]}
                                    fill={[{ match: { id: 'This period' }, id: 'revenueGradient' }]}
                                    enableGridX={false}
                                    gridYValues={5}
                                    theme={{
                                        grid: { line: { stroke: '#F1F5F9', strokeDasharray: '3 3' } },
                                        axis: { ticks: { text: { fontSize: 12, fill: TEXT_MUTED } } },
                                        tooltip: { container: { fontSize: 13, borderRadius: 8 } },
                                    }}
                                    axisLeft={{ format: v => fmt(v), tickValues: 5 }}
                                    axisBottom={{
                                        tickRotation: 0,
                                        tickValues: data.trend
                                            .filter((_, i) => i % Math.ceil(data.trend.length / 8) === 0)
                                            .map(t => t.label),
                                    }}
                                    animate={true}
                                    motionConfig="gentle"
                                    enableSlices="x"
                                    sliceTooltip={({ slice }) => (
                                        <div style={{ background: '#fff', padding: '10px 14px', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', fontSize: 12.5, width: 190 }}>
                                            <div style={{ fontWeight: 700, marginBottom: 8, whiteSpace: 'nowrap' }}>{slice.points[0].data.xFormatted}</div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '12px auto 1fr', columnGap: 8, rowGap: 4, alignItems: 'center' }}>
                                                {slice.points.map(p => (
                                                    <Fragment key={p.seriesId}>
                                                        <span style={{ width: 8, height: 8, borderRadius: 4, background: p.seriesColor, display: 'inline-block' }} />
                                                        <span style={{ color: '#64748B', whiteSpace: 'nowrap' }}>{p.seriesId}</span>
                                                        <strong style={{ color: '#1E293B', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(p.data.y)}</strong>
                                                    </Fragment>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    legends={[{
                                        anchor: 'top-right', direction: 'row', translateY: -20, itemWidth: 90, itemHeight: 20,
                                        symbolSize: 10, symbolShape: 'circle', itemTextColor: '#475569',
                                    }]}
                                />
                            </div>
                        </div>

                        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '18px 20px' }}>
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0F1F2E' }}>Where the sales come from</h3>
                            <p style={{ margin: '2px 0 12px', fontSize: 12, color: '#8C82A0' }}>Revenue by channel, biggest first</p>
                            {topPlatforms.length === 0 ? <p className="empty">No data</p> : (
                                <div style={{ height: Math.max(180, topPlatforms.length * 42) }}>
                                    <ResponsiveBar
                                        data={[...topPlatforms].reverse()}
                                        keys={['revenue']}
                                        indexBy="platform"
                                        layout="horizontal"
                                        margin={{ top: 6, right: 60, bottom: 24, left: 100 }}
                                        padding={0.35}
                                        colors={({ indexValue }) => CHANNEL_COLORS[topPlatforms.findIndex(p => p.platform === indexValue) % CHANNEL_COLORS.length]}
                                        borderRadius={4}
                                        enableGridY={false}
                                        enableGridX={true}
                                        enableLabel={false}
                                        axisBottom={{ format: v => fmt(v), tickValues: 4 }}
                                        axisLeft={{ tickSize: 0, tickPadding: 8 }}
                                        theme={{
                                            grid: { line: { stroke: '#F1F5F9', strokeDasharray: '3 3' } },
                                            axis: { ticks: { text: { fontSize: 12, fill: TEXT_MUTED } } },
                                        }}
                                        animate={true}
                                        motionConfig="gentle"
                                        layers={['grid', 'axes', 'bars', 'markers', 'legends', 'annotations', (layerProps) => (
                                            <g>
                                                {layerProps.bars.map(bar => (
                                                    <text
                                                        key={bar.key}
                                                        x={bar.x + bar.width + 8}
                                                        y={bar.y + bar.height / 2}
                                                        dominantBaseline="central"
                                                        textAnchor="start"
                                                        style={{ fontSize: 12, fontWeight: 700, fill: '#1E293B' }}
                                                    >
                                                        {fmt(bar.data.value)}
                                                    </text>
                                                ))}
                                            </g>
                                        )]}
                                        tooltip={({ indexValue, value }) => {
                                            const growth = growthByPlatform[indexValue];
                                            return (
                                                <div style={{ background: '#fff', padding: '8px 12px', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', fontSize: 12.5 }}>
                                                    <div style={{ fontWeight: 700, marginBottom: 2 }}>{indexValue}</div>
                                                    <div style={{ color: '#1E293B' }}>
                                                        {fmt(value)}
                                                        {growth != null && <span style={{ color: growth >= 0 ? '#10B981' : '#EF4444', fontWeight: 700, marginLeft: 6 }}>({growth > 0 ? '+' : ''}{growth}%)</span>}
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* minWidth:0 on every grid child — without it, a CSS grid track
                        never shrinks below its content's natural width (the movers
                        rows' fixed-width qty columns), so the whole row forces the
                        page into horizontal overflow instead of the columns
                        compressing to fit. */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, marginBottom: 20 }}>
                        <div style={{ minWidth: 0, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '18px 20px' }}>
                            <h3 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700, color: '#0F1F2E' }}>Top 10 Fastest Growing SKU</h3>
                            {data.risers.length === 0 ? <p className="empty">No comparable data yet</p> : (
                                <>
                                    <MoverHeader />
                                    {data.risers.map((r, i) => <MoverRow key={i} r={r} positive />)}
                                </>
                            )}
                        </div>

                        <div style={{ minWidth: 0, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '18px 20px' }}>
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0F1F2E' }}>Sales by Region (QC)</h3>
                            <p style={{ margin: '2px 0 12px', fontSize: 12, color: '#8C82A0' }}>
                                Based on {locations?.coverage_pct ?? '—'}% of sales with known region
                            </p>
                            {!locations || locations.regions.length === 0 ? <p className="empty">No data</p> : (
                                <div style={{ height: 240 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={locations.regions}
                                                dataKey="share"
                                                nameKey="region"
                                                innerRadius="45%"
                                                outerRadius="75%"
                                                paddingAngle={2}
                                                stroke="#fff"
                                                strokeWidth={2}
                                                isAnimationActive
                                                label={({ name, value }) => `${name} ${value}%`}
                                                labelLine={false}
                                            >
                                                {locations.regions.map((r, i) => (
                                                    <Cell key={r.region} fill={r.region === 'Others' ? '#C9C4D6' : CHANNEL_COLORS[i % CHANNEL_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip
                                                contentStyle={{ background: '#fff', borderRadius: 8, border: '1px solid #EFEAF6', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', fontSize: 12.5 }}
                                                formatter={(value, name, props) => [`${props.payload.qty.toLocaleString('en-IN')} units (${value}%)`, name]}
                                            />
                                            <Legend wrapperStyle={{ fontSize: 11.5 }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ width: '40%', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '18px 20px' }}>
                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0F1F2E' }}>Top 5 Cities (QC)</h3>
                        <p style={{ margin: '2px 0 14px', fontSize: 12, color: '#8C82A0' }}>By units sold</p>
                        {!locations || locations.top_cities.length === 0 ? <p className="empty">No comparable data yet</p> : (() => {
                            const maxShare = locations.top_cities[0].share || 1;
                            return locations.top_cities.map((c, i) => (
                                <div key={c.city} style={{ marginBottom: 12 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                                        <span style={{ fontWeight: 600, color: '#1E293B' }}>{c.city}</span>
                                        <span style={{ color: '#8C82A0' }}>{c.qty.toLocaleString('en-IN')} units <strong style={{ color: CHANNEL_COLORS[i % CHANNEL_COLORS.length] }}>{c.share}%</strong></span>
                                    </div>
                                    <div style={{ height: 6, borderRadius: 999, background: '#F1EDF9', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${(c.share / maxShare) * 100}%`, borderRadius: 999, background: CHANNEL_COLORS[i % CHANNEL_COLORS.length] }} />
                                    </div>
                                </div>
                            ));
                        })()}
                    </div>
                </>
            )}
                </div>
            </div>
        </div>
    );
}
